<template>
	<div class="drug">
		<v-list-item
			:key="drug.id"
			color="primary"
			style="border: 1px solid #e0e0e0; border-radius: 5px"
		>
			<v-list-item-icon class="mr-3">
				<v-icon color="primary">mdi-pill</v-icon>
			</v-list-item-icon>
			<v-list-item-content>
				<v-list-item-title>
					<a @click="showDetails = true">
						<span class="mr-4 primary--text">
							<strong>{{drug.name}}</strong>
						</span>
					</a>
				</v-list-item-title>
				<v-list-item-subtitle>{{drug.dosage}}</v-list-item-subtitle>
				<v-dialog v-model="showDetails" max-width="360">
					<v-card>
						<v-card-title class="title primary--text mb-2 secondary" v-translate>Drug Info</v-card-title>

						<v-card-text>
							<p>
								<strong v-translate>Name:</strong>
								{{drug.name}}
							</p>
							<p>
								<strong v-translate>Generic Name:</strong>
								{{drug.generic_name}}
							</p>
							<p>
								<strong v-translate>Presentation:</strong>
								{{drug.farm_formula}}
							</p>
							<p>
								<strong v-translate>Dosage:</strong>
								{{drug.dosage}}
							</p>
							<p>
								<strong v-translate>Generic:</strong>
								<span class="ml-1" v-if="drug.is_generic" v-translate>Yes</span>
								<span class="ml-1" v-else v-translate>No</span>
							</p>
							<p>
								<strong v-translate>Owner:</strong>
								{{drug.owner}}
							</p>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-btn v-if="editable" color="primary darken-1" text @click="showDetails = false; removing = true; $emit('remove-drug', drug)" :disabled="disabled" :loading="removing">
								<translate>Remove</translate>
							</v-btn>
							<v-spacer></v-spacer>
							<v-btn color="primary darken-1" text @click="showDetails = false">
								<translate>Close</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</v-list-item-content>
			<v-list-item-action class="primary--text">
				<v-row no-gutters align="center">
					<v-text-field
						v-if="context == 'appointment'"
						:placeholder="$gettext('Info')"
						class="mr-3"
						v-model="drug.info"
						@blur="$emit('edit-drug', drug)"
						outlined
						dense
						hide-details
						style="width: 400px"
						:disabled="disabled"
					></v-text-field>
					<template v-if="context == 'new-product'">
						<v-chip v-if="drug.interaction.medicine_interaction_id" small class="mr-1">{{interactionName}}</v-chip>
						<v-chip v-if="drug.interaction.info_pt" small class="mr-1">{{drug.interaction.info_pt}}</v-chip>
					</template>
					<v-btn v-if="editable && context == 'new-product'" icon @click="showDrugInteractionOptions = true">
						<v-icon color="primary">edit</v-icon>
					</v-btn>
					<v-btn v-if="context == 'admin'" icon :to="{name: 'edit-drug', params: {did: drug.id}}">
						<v-icon color="primary">edit</v-icon>
					</v-btn>
					<v-btn v-if="editable" icon @click="removing = true; $emit('remove-drug', drug)" :disabled="disabled" :loading="removing">
						<v-icon color="primary">delete</v-icon>
					</v-btn>
				</v-row>
			</v-list-item-action>
		</v-list-item>
		<v-dialog v-if="context == 'new-product'" v-model="showDrugInteractionOptions" width="50%" scrollable>
			<v-card>
				<v-card-title class="title primary--text mb-2 secondary">
					<translate>Drug Interaction</translate>
					<span class="ml-1">- {{drug.name}}</span>
				</v-card-title>
				<v-card-text>
					<v-row no-gutters class="mt-3">
						<v-col cols="12">
							<v-select
								v-model="drug.interaction.medicine_interaction_id"
								:items="drugInteractions"
								:label="$gettext('Drug Interaction')"
								item-text="name"
								item-value="id"
								outlined
								class="px-2"
							></v-select>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-row no-gutters>
								<v-col cols="6">
									<v-text-field
										v-model="drug.interaction.no_effect_quantity"
										:label="$gettext('No effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="drug.interaction.minimum_effect_quantity"
										:label="$gettext('Minimum effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="drug.interaction.moderate_effect_quantity"
										:label="$gettext('Moderate effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="drug.interaction.high_effect_quantity"
										:label="$gettext('High effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
							</v-row>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="drug.interaction.info_pt"
								:label="$gettext('Info') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.info_en"
								:label="$gettext('Info') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.info_es"
								:label="$gettext('Info') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="drug.interaction.endpoint_pt"
								:label="$gettext('Endpoint') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.endpoint_en"
								:label="$gettext('Endpoint') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.endpoint_es"
								:label="$gettext('Endpoint') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="drug.interaction.result_pt"
								:label="$gettext('Result') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.result_en"
								:label="$gettext('Result') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.result_es"
								:label="$gettext('Result') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="drug.interaction.recommendation_pt"
								:label="$gettext('Recommendation') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.recommendation_en"
								:label="$gettext('Recommendation') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="drug.interaction.recommendation_es"
								:label="$gettext('Recommendation') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<SearchBibliographicReferences class="px-2" :selectedBibliographicReferences="drug.interaction.bibliographic_references" />
						</v-col>
					</v-row>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary darken-1" text @click="showDrugInteractionOptions = false">
						<translate>Save</translate>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import SearchBibliographicReferences from "@/components/bibliographic_references/SearchBibliographicReferences.vue";

export default {
	name: "DrugItem",
	props: {
		drug: {},
		editable: { default: true },
		context: {},
		drugInteractions: {},
		disabled: {
			default: false
		}
	},
	components: {
		SearchBibliographicReferences
	},
	data: () => ({
		showDetails: false,
		showDrugInteractionOptions: false,
		removing: false
	}),
	computed: {
		interactionName() {
			if(!this.drug.interaction) return null;
			for(let i = 0; i < this.drugInteractions.length; i++) {
				if(this.drugInteractions[i].id == this.drug.interaction.medicine_interaction_id) return this.drugInteractions[i].name;
			}
			return null;
		}
	}
};
</script>